import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { jobs } from "../../../static/jobsData";
import {
  getActiveJobs, autoSections, findJobBySlug, getRelatedJobs,
} from "../../../static/jobsHelpers";
import "../styles/careers.css";

import Breadcrumb from "./01_breadcrumb";
import JobHeader from "./02_jobHeader";
import MainContent from "./03_mainContent";
import RelatedJobs from "./04_relatedJobs";

// NOTE: no <header>/<footer> here — plug in your existing site Header/Footer
// around this page in your router layout, same as the other two career pages.
//
// ROUTING: the old HTML version read the job from a ?job=slug query string.
// This version uses a normal route param instead — cleaner with React Router:
//   <Route path="/careers/:slug" element={<CareerDetails />} />
// Every "View Details" link (JobCard, RelatedJobCard) already points to
// `/careers/${job.slug}` to match.

export default function CareerDetails() {
  const { slug } = useParams();
  const [copied, setCopied] = useState(false);

  let job = slug ? findJobBySlug(jobs, slug) : null;
  // Fallback: no slug matched (e.g. a stale/removed link) — show the first
  // active job in sort order instead of a blank page.
  if (!job) job = getActiveJobs(jobs)[0];

  // Keep the browser tab title in sync with the job being viewed.
  useEffect(() => {
    if (!job) return;
    document.title = `${job.title} | Careers at VerdeMobility`;
  }, [job]);

  if (!job) {
    return (
      <div className="vm-careers">
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-3">No open positions right now</h1>
          <p className="text-[var(--vm-text-gray)]">
            Please check back soon, or view <Link to="/careers/openings">all careers</Link>.
          </p>
        </div>
      </div>
    );
  }

  if (job.status === "discontinued" || job.status === "draft") {
    return (
      <div className="vm-careers">
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-3">This position is no longer open</h1>
          <p className="text-[var(--vm-text-gray)] mb-6">
            "{job.title}" is currently not accepting applications. Explore our other current
            openings below.
          </p>
          <Link to="/careers/openings" className="btn-brand">View All Openings</Link>
        </div>
      </div>
    );
  }

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${job.title} at VerdeMobility — ${shareUrl}`)}`;

  async function handleShare() {
    const shareData = {
      title: job.title,
      text: `Check out this job opening: ${job.title}`,
      url: shareUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // AbortError fires when the user cancels the share sheet — safe to ignore
        if (err.name !== "AbortError") console.error("Share failed:", err);
      }
    } else {
      // Fallback for browsers without the Web Share API
      window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    }
  }

  async function handleCopyId(value) {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard API unavailable — fail quietly, the Job ID is already
      // shown as plain text right next to the button.
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const allSections = [...(job.sections || []), ...autoSections(job)];
  const overviewRows = [{ label: "Job ID", value: job.id }, ...(job.overview || [])];
  const relatedJobs = getRelatedJobs(jobs, job);

  return (
    <div className="vm-careers mt-[125px]">
      <Breadcrumb job={job} />
      <JobHeader job={job} onShare={handleShare} />
      <MainContent
        job={job}
        allSections={allSections}
        overviewRows={overviewRows}
        copied={copied}
        onCopyId={handleCopyId}
        onShare={handleShare}
      />
      <RelatedJobs relatedJobs={relatedJobs} />
    </div>
  );
}
